import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { AngularMaterialModule } from '../angular-material.module'; // adjust path if needed
import { TenantBrandingService } from '../tenant/tenant-branding.service';
import { TenantShellBranding } from '../tenant/tenant.model';
import { TenantFeatureService } from '../tenant/tenant-feature.service';
import { TenantPageVisibilityService, TenantPublicPage } from '../tenant/tenant-page-visibility.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AngularMaterialModule
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy {
  readonly mobileMenuId = 'mobile-navigation-menu';
  @ViewChild('mobileMenuButton', { read: ElementRef }) private mobileMenuButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('mobileMenu', { read: ElementRef }) private mobileMenu?: ElementRef<HTMLElement>;

  userIsAuthenticated = false;
  userRole = '';
  userEmail = '';
  isMenuOpen = false;
  isMenuClosing = false;
  isLeagueMenuOpen = false;
  isAdminSettingsMenuOpen = false;
  isAdminRoute = false;
  branding: TenantShellBranding;
  private authListenerSubs: Subscription;
  private routerSubs: Subscription;
  private brandingSubs: Subscription;
  private featureSubs: Subscription;
  private pageVisibilitySubs: Subscription;
  private menuFocusTimer: ReturnType<typeof setTimeout> | null = null;
  private menuCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private tenantBranding: TenantBrandingService,
    private tenantFeatures: TenantFeatureService,
    private tenantPageVisibility: TenantPageVisibilityService,
  ) {
    this.branding = this.tenantBranding.currentBranding;
  }

  ngOnInit() {
    this.userIsAuthenticated = this.authService.getIsAuth();
    this.userRole = this.authService.getUserRole();
    this.userEmail = this.authService.getUserEmail();

    this.authListenerSubs = this.authService.getAuthStatusListener().subscribe(userInfo => {
      this.userIsAuthenticated = userInfo.isAuthenticated;
      this.userRole = userInfo.role;
      this.userEmail = userInfo.email;
    });
    this.brandingSubs = this.tenantBranding.branding$.subscribe(branding => {
      this.branding = branding;
    });
    this.featureSubs = this.tenantFeatures.enabledModules$.subscribe();
    this.pageVisibilitySubs = this.tenantPageVisibility.pageVisibility$.subscribe();

    // Check initial route
    this.checkAdminRoute(this.router.url);

    // Subscribe to route changes
    this.routerSubs = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkAdminRoute(event.url);
      });
  }

  isFeatureEnabled(moduleName: Parameters<TenantFeatureService['isEnabled']>[0]): boolean {
    return this.tenantFeatures.isEnabled(moduleName);
  }

  isLeagueEnabled(): boolean {
    return this.tenantFeatures.isPublicLeagueEnabled() && this.isPageVisible('league');
  }

  isPageVisible(page: TenantPublicPage): boolean {
    return this.tenantPageVisibility.isVisible(page);
  }

  private checkAdminRoute(url: string) {
    const adminRoutes = [
      '/players',
      '/add-player',
      '/team',
      '/import-players',
      '/edit-player',
      '/teams',
      '/add-team',
      '/edit-team',
      '/import-teams',
      '/division-content',
      '/season-content',
      '/schedule-content',
      '/payment-content',
      '/payroll-content',
      '/payment',
      '/my-dashboard',
      '/league',
    ];
    this.isAdminRoute = adminRoutes.some(route => url.startsWith(route));
  }

  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.clearMenuTimers();
      this.isMenuOpen = true;
      this.isMenuClosing = false;
      this.menuFocusTimer = setTimeout(() => this.focusFirstMenuControl());
    }
  }

  closeMenu() {
    if (!this.isMenuOpen || this.isMenuClosing) return;
    if (this.menuFocusTimer) {
      clearTimeout(this.menuFocusTimer);
      this.menuFocusTimer = null;
    }
    this.isMenuClosing = true;
    this.menuCloseTimer = setTimeout(() => {
      this.isMenuOpen = false;
      this.isMenuClosing = false;
      this.isLeagueMenuOpen = false;
      this.isAdminSettingsMenuOpen = false;
      this.menuCloseTimer = null;
      this.mobileMenuButton?.nativeElement?.focus();
    }, 300);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (!this.isMenuOpen) return;
    event.preventDefault();
    this.closeMenu();
  }

  onMobileMenuKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const controls = this.mobileMenuControls();
    if (!controls.length) return;

    const activeElement = document.activeElement;
    const firstControl = controls[0];
    const lastControl = controls[controls.length - 1];
    if (event.shiftKey && (activeElement === firstControl || !this.mobileMenu?.nativeElement.contains(activeElement))) {
      event.preventDefault();
      lastControl.focus();
    } else if (!event.shiftKey && (activeElement === lastControl || !this.mobileMenu?.nativeElement.contains(activeElement))) {
      event.preventDefault();
      firstControl.focus();
    }
  }

  private focusFirstMenuControl(): void {
    this.menuFocusTimer = null;
    this.mobileMenuControls()[0]?.focus();
  }

  private mobileMenuControls(): HTMLElement[] {
    const menu = this.mobileMenu?.nativeElement;
    if (!menu) return [];
    return Array.from(menu.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }

  private clearMenuTimers(): void {
    if (this.menuFocusTimer) clearTimeout(this.menuFocusTimer);
    if (this.menuCloseTimer) clearTimeout(this.menuCloseTimer);
    this.menuFocusTimer = null;
    this.menuCloseTimer = null;
  }

  toggleLeagueMenu() {
    this.isLeagueMenuOpen = !this.isLeagueMenuOpen;
  }

  toggleAdminSettingsMenu() {
    this.isAdminSettingsMenuOpen = !this.isAdminSettingsMenuOpen;
  }

  ngOnDestroy() {
    this.clearMenuTimers();
    this.authListenerSubs.unsubscribe();
    if (this.brandingSubs) {
      this.brandingSubs.unsubscribe();
    }
    if (this.routerSubs) {
      this.routerSubs.unsubscribe();
    }
    if (this.featureSubs) {
      this.featureSubs.unsubscribe();
    }
    if (this.pageVisibilitySubs) {
      this.pageVisibilitySubs.unsubscribe();
    }
  }
}
